<template>
    <div class="ds-dossier-week-nav">
        <span class="ds-dossier-week-title">
            <button type="button" class="ds-dossier-icon-button" aria-label="Предыдущая неделя" @click="shiftWeek(-1)">
                <svg width="9" height="13" viewBox="0 0 9 13" fill="currentColor" aria-hidden="true"><path d="M3.37716 6.5002L8.12091 11.244L6.76583 12.599L0.666992 6.5002L6.76583 0.401367L8.12091 1.75645L3.37716 6.5002Z" /></svg>
            </button>
            <span>{{ weekLabel }}</span>
            <button type="button" class="ds-dossier-icon-button" aria-label="Следующая неделя" @click="shiftWeek(1)">
                <svg width="9" height="13" viewBox="0 0 9 13" fill="currentColor" aria-hidden="true"><path d="M5.62363 6.5002L0.879883 1.75645L2.23497 0.401367L8.3338 6.5002L2.23497 12.599L0.879883 11.244L5.62363 6.5002Z" /></svg>
            </button>
        </span>
        <span v-if="weekState.status === 'ready'" class="ds-dossier-muted">{{ weekTotals }}</span>
    </div>

    <DossierState :status="weekState.status" :height="220" error-text="Не удалось загрузить смены" @retry="retryWeek" />
    <template v-if="weekState.status === 'ready'">
        <div v-if="!rows.length" class="ds-dossier-state is-plain">На этой неделе смен нет</div>
        <!-- Строки не открывают редактор и не ведут в расписание (оба контекста). -->
        <div v-else class="ds-dossier-rows">
            <div v-for="row in rows" :key="row.key" class="ds-dossier-row" :class="{ 'is-today': row.isToday, 'is-future': row.isFuture }">
                <span class="ds-dossier-row-date">{{ row.label }}</span>
                <span class="ds-dossier-row-shifts">
                    <span v-for="line in visibleLines(row)" :key="line.key" class="ds-dossier-row-line">
                        <template v-if="line.dayState">
                            <span class="ds-dossier-pill" style="background: var(--color-day-state-bg); color: var(--color-text-main)">{{ line.dayState }}</span>
                        </template>
                        <template v-else>
                            <span class="ds-dossier-row-plan">{{ line.plan }}</span>
                            <span class="ds-dossier-row-fact">
                                <span v-if="line.noMarks" class="is-absent">нет отметок</span>
                                <template v-else-if="line.factStart">
                                    <span :class="{ 'is-violated': line.startViolated }">{{ line.factStart }}</span>–<span :class="{ 'is-violated': line.endViolated }">{{ line.factEnd }}</span>
                                </template>
                            </span>
                            <span v-for="chip in line.chips" :key="chip.text" class="ds-dossier-pill" :class="chip.className" :title="chip.title">{{ chip.text }}</span>
                        </template>
                    </span>
                    <button
                        v-if="row.lines.length > LINES_LIMIT"
                        type="button"
                        class="ds-dossier-row-more"
                        :aria-expanded="expanded.has(row.key)"
                        @click="toggleExpanded(row.key)"
                    >{{ expanded.has(row.key) ? 'Свернуть' : `Ещё ${row.lines.length - LINES_LIMIT}` }}</button>
                </span>
                <span class="ds-dossier-row-hours">{{ row.hours }}</span>
            </div>
        </div>
    </template>

    <!-- Нарушения месяца начала показанной недели: discipline по всем доступным ТУ. Строки без клика. -->
    <template v-if="showViolations">
        <div class="ds-dossier-section-head">
            <span class="ds-dossier-section-title">Нарушения {{ monthPrepositional(violationsMonth) }}</span>
            <span class="ds-dossier-muted">{{ violationsCountText }}</span>
        </div>
        <DossierState :status="disciplineState.status" :height="96" error-text="Не удалось загрузить нарушения" @retry="retryDiscipline" />
        <template v-if="disciplineState.status === 'ready'">
            <div v-if="!events.length" class="ds-dossier-state is-plain">Нарушений за месяц нет</div>
            <div v-else class="ds-dossier-rows">
                <div v-for="event in events" :key="event.shiftId" class="ds-dossier-row">
                    <span class="ds-dossier-row-date">{{ eventDate(event.date) }}</span>
                    <span class="ds-dossier-row-line">
                        <span class="ds-dossier-row-plan">{{ timeRange(event.planStart, event.planEnd) ?? DASH }}</span>
                        <span class="ds-dossier-row-fact">
                            <span v-if="event.kind === 'absence'" class="is-absent">нет отметок</span>
                            <span v-else>{{ timeRange(event.factStart, event.factEnd) ?? DASH }}</span>
                        </span>
                        <span
                            v-for="label in disciplineLabels(event)"
                            :key="label"
                            class="ds-dossier-pill"
                            :class="event.kind === 'no_marks' ? 'ds-dossier-pill-no-marks' : 'ds-dossier-pill-violation'"
                            :title="event.departmentName ?? undefined"
                        >{{ label }}</span>
                    </span>
                    <span class="ds-dossier-row-hours">{{ formatDurationHours(event.duration) }}</span>
                </div>
            </div>
            <button v-if="moreCount > 0" type="button" class="ds-dossier-link" @click="emit('open-discipline', violationsMonth)">Ещё {{ moreCount }} ›</button>
        </template>
    </template>
</template>

<script setup lang="ts">
import { computed, reactive, watch, watchEffect } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import type { Shift } from '@/types'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { describeShiftTiming, formatWeekPeriod } from '@/components/schedule/weekSchedule'
import {
    type DossierEmployment,
    DASH,
    DATE_KEY,
    formatDurationHours,
    formatHours,
    hasPlan,
    monthPrepositional,
    pluralShifts,
    shiftDurationHours,
    shiftsByDate,
} from './dossierModel'
import { disciplineLabels, monthParam, timeRange } from './dossierActivity'
import type { DossierData } from './useDossierData'
import DossierState from './DossierState.vue'

// Поля ShiftsForSchedule, отсутствующие в общем типе Shift (main.api_schema.ShiftsForSchedule).
interface ScheduleShift extends Shift {
    is_not_main_position?: boolean
    is_not_main_department?: boolean
}

const props = defineProps<{
    data: DossierData
    employment: DossierEmployment
    today: Dayjs
    now: Dayjs
}>()
const weekStart = defineModel<Dayjs>('weekStart', { required: true })
const emit = defineEmits<{ 'open-discipline': [month: Dayjs] }>()

const scheduleStore = useScheduleStore()
const departmentStore = useDepartmentStore()
const LINES_LIMIT = 2

const range = computed(() => ({ start: weekStart.value.format(DATE_KEY), end: weekStart.value.add(6, 'day').format(DATE_KEY) }))
const weekLabel = computed(() => formatWeekPeriod(weekStart.value))
const shiftWeek = (delta: number) => {
    weekStart.value = weekStart.value.add(delta, 'week')
}

// Неделя: переиспользуется загруженное покрытие (например, месяц сводки), иначе — запрос границ недели.
const weekState = computed(() => props.data.readSchedule(props.employment.id, range.value.start, range.value.end))
const retryWeek = () => props.data.retrySchedule(props.employment.id, range.value.start, range.value.end)

// На стыке месяцев — месяц начала показанной недели.
const violationsMonth = computed(() => weekStart.value.startOf('month'))
const disciplineQuery = computed(() => ({ from: monthParam(violationsMonth.value), to: monthParam(violationsMonth.value), page: 1 }))
const disciplineState = computed(() => props.data.discipline.read(disciplineQuery.value))
const retryDiscipline = () => props.data.discipline.retry(disciplineQuery.value)

watchEffect(() => {
    props.data.ensureSchedule(props.employment.id, range.value.start, range.value.end)
    props.data.discipline.ensure(disciplineQuery.value)
})

// Раскрытие длинного дня внутри строки; сбрасывается при смене недели/ТУ.
const expanded = reactive(new Set<string>())
const toggleExpanded = (key: string) => (expanded.has(key) ? expanded.delete(key) : expanded.add(key))
watch(() => [props.employment.id, range.value.start], () => expanded.clear())

const positionName = (id: string | null | undefined) => scheduleStore.positions.find(item => item.id === id)?.name
const departmentName = (id: string | null | undefined) => departmentStore.departments.find(item => item.id === id)?.name
const dayTypeName = (id: string | null | undefined) => scheduleStore.dayTypes.find(item => item.id === id)?.name

interface Line {
    key: string
    dayState?: string
    plan: string
    factStart: string
    factEnd: string
    noMarks: boolean
    startViolated: boolean
    endViolated: boolean
    chips: { text: string, className: string, title?: string }[]
}

function lineOf(shift: ScheduleShift, index: number): Line {
    const timing = describeShiftTiming(shift, props.now, undefined)
    const chips: Line['chips'] = []
    const extraParts = [
        shift.is_overtime ? 'доп.' : '',
        shift.is_not_main_position ? (positionName(shift.position) ?? 'другая должность') : '',
        shift.is_not_main_department ? (departmentName(shift.department) ?? 'другое подразделение') : '',
    ].filter(Boolean)
    if (extraParts.length) chips.push({ text: extraParts.join(' · '), className: 'ds-dossier-pill-extra' })
    if (shift.is_vacancy) chips.push({ text: 'биржа', className: 'ds-dossier-pill-smart' })
    if (timing.isNoMarks) chips.push({ text: 'Нет отметок', className: 'ds-dossier-pill-no-marks' })
    else if (timing.violationCount > 0 || timing.hasGeneralViolation) {
        // violation — свободный object: тип/минуты не выводятся, только сообщения сервера в подсказке.
        chips.push({ text: 'Нарушение', className: 'ds-dossier-pill-violation', title: timing.violationMessages.join('\n') || undefined })
    }
    return {
        key: shift.id ?? `${shift.date}-${index}`,
        plan: `${timing.planStart}–${timing.planEnd}`,
        factStart: timing.isUpcoming ? '' : timing.factStart,
        factEnd: timing.factEnd,
        noMarks: timing.isNoMarks,
        startViolated: timing.startViolated,
        endViolated: timing.endViolated,
        chips,
    }
}

const rows = computed(() => {
    const byDate = shiftsByDate(weekShifts.value as ScheduleShift[])
    const todayKey = props.today.format(DATE_KEY)
    const result = []
    for (let index = 0; index < 7; index++) {
        const date = weekStart.value.add(index, 'day')
        const key = date.format(DATE_KEY)
        const shifts = (byDate.get(key) ?? []) as ScheduleShift[]
        const planned = shifts.filter(hasPlan)
        const dayState = shifts.find(shift => shift.day_state_document)
        if (!planned.length && !dayState) continue
        const lines: Line[] = planned.map(lineOf)
        if (!planned.length && dayState) {
            lines.push({ key: `${key}-state`, dayState: dayTypeName(dayState.day_type) ?? 'Кадровое состояние', plan: '', factStart: '', factEnd: '', noMarks: false, startViolated: false, endViolated: false, chips: [] })
        }
        const inProgress = planned.some(shift => describeShiftTiming(shift, props.now, undefined).isInProgress)
        const known = planned.map(shiftDurationHours).filter((value): value is number => value !== null)
        // Неизвестные часы — «—»; длительность не пересчитывается разностью времён.
        const hours = inProgress ? 'идёт' : known.length && known.length === planned.length ? formatDurationHours(known.reduce((a, b) => a + b, 0)) : DASH
        const label = date.locale('ru').format('dd, D')
        result.push({ key, label: label.charAt(0).toUpperCase() + label.slice(1), isToday: key === todayKey, isFuture: key > todayKey, lines, hours })
    }
    return result
})

const visibleLines = (row: { key: string, lines: Line[] }) => (
    expanded.has(row.key) || row.lines.length <= LINES_LIMIT ? row.lines : row.lines.slice(0, LINES_LIMIT)
)

// Итоги только смен показанной недели: блок может прийти из более широкого покрытия (месяц сводки).
// Неизвестная длительность хотя бы одной смены делает неизвестным и итог часов — частичная сумма не выдаётся за полную.
const weekShifts = computed(() => (weekState.value.data?.shifts ?? [])
    .filter(shift => Boolean(shift.date) && shift.date! >= range.value.start && shift.date! <= range.value.end))
const weekTotals = computed(() => {
    const planned = weekShifts.value.filter(hasPlan)
    const durations = planned.map(shiftDurationHours)
    const hours = !durations.length ? '0 ч' : durations.every(value => value !== null)
        ? `${formatHours((durations as number[]).reduce((a, b) => a + b, 0))} ч`
        : `${DASH} ч`
    return `${pluralShifts(planned.length)} · ${hours}`
})

// ---------- Нарушения месяца ----------
const VIOLATIONS_LIMIT = 3
const page = computed(() => disciplineState.value.data)
const violationsCount = computed(() => page.value?.totals.violationsCount ?? null)
const events = computed(() => (page.value?.results ?? []).slice(0, VIOLATIONS_LIMIT))
// «Ещё N» — строки периода (count) минус показанные; не violations_count минус три.
const moreCount = computed(() => Math.max(0, (page.value?.count ?? 0) - events.value.length))
// Реальное 0 violations_count — блок скрыт (отдельные no_marks остаются во вкладке «Дисциплина»).
const showViolations = computed(() => !(disciplineState.value.status === 'ready' && violationsCount.value === 0))
const violationsCountText = computed(() => {
    const state = disciplineState.value
    if (state.status === 'forbidden') return 'всего: недоступно'
    return `всего: ${violationsCount.value ?? DASH}`
})

const eventDate = (value: string) => {
    const label = dayjs(value).locale('ru').format('dd, D')
    return label.charAt(0).toUpperCase() + label.slice(1)
}
</script>
